import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/Button';
import { useLanguage } from './LanguageContext';
import { Layers, Building2, Laptop, Workflow } from 'lucide-react';
interface SlideNewCategoryProps {
  onContinue: () => void;
}
export function SlideNewCategory({
  onContinue
}: SlideNewCategoryProps) {
  const {
    t
  } = useLanguage();
  const [stage, setStage] = useState(0);
  useEffect(() => {
    const t1 = setTimeout(() => setStage(1), 300);
    const t2 = setTimeout(() => setStage(2), 900);
    const t3 = setTimeout(() => setStage(3), 1700);
    const t4 = setTimeout(() => setStage(4), 2400);
    const t5 = setTimeout(() => setStage(5), 3100);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      clearTimeout(t4);
      clearTimeout(t5);
    };
  }, []);
  return <div className="h-full w-full flex flex-col items-center justify-center px-8 py-10 text-center relative overflow-hidden" data-id="element-651">
      {/* Soft ambient glow */}
      <div className="absolute inset-0 z-0 flex items-center justify-center pointer-events-none" data-id="element-652">
        <div className="w-[700px] h-[700px] bg-teal-400/5 rounded-full blur-[100px]" data-id="element-653" />
      </div>

      <div className="z-10 max-w-4xl w-full flex flex-col items-center" data-id="element-654">
        {/* Layers icon */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.8,
        y: -10
      }} animate={{
        opacity: stage >= 1 ? 1 : 0,
        scale: stage >= 1 ? 1 : 0.8,
        y: stage >= 1 ? 0 : -10
      }} transition={{
        duration: 0.6
      }} className="mb-10" data-id="element-655">
          <div className="w-16 h-16 rounded-2xl bg-navy-700/60 border border-teal-400/30 flex items-center justify-center backdrop-blur-sm" data-id="element-656">
            <Layers className="w-7 h-7 text-teal-400" strokeWidth={2.2} data-id="element-657" />
          </div>
        </motion.div>

        {/* Eyebrow */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="text-center space-y-4" data-id="element-658">
          <p className="text-teal-400 font-semibold tracking-widest uppercase text-sm" data-id="element-659">
            {t('The Shift', 'Skiftet')}
          </p>
          <h2 className="text-4xl md:text-5xl font-serif text-white" data-id="element-660">
            {t('A New Category', 'En ny kategori')}
          </h2>
        </motion.div>

        {/* Headline */}
        <motion.h2 initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: stage >= 3 ? 1 : 0,
        y: stage >= 3 ? 0 : 20
      }} transition={{
        duration: 0.6
      }} className="text-5xl md:text-7xl lg:text-8xl font-sans font-bold text-white leading-[1.05] tracking-tight mb-8" data-id="element-661">
          Campaign Service
          <br data-id="element-662" />
          Management
        </motion.h2>

        {/* Gradient divider */}
        <motion.div initial={{
        width: 0,
        opacity: 0
      }} animate={{
        width: stage >= 4 ? 180 : 0,
        opacity: stage >= 4 ? 1 : 0
      }} transition={{
        duration: 0.7
      }} className="h-[3px] bg-gradient-to-r from-blue-400 via-teal-400 to-teal-300 rounded-full mb-8" data-id="element-663" />

        {/* Subtitle */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="bg-gradient-to-r from-teal-400/20 via-purple-500/20 to-teal-400/20 border border-teal-400/30 rounded-2xl p-10 text-center relative overflow-hidden" data-id="element-664">
          <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10" data-id="element-665" />
          <div className="relative z-10" data-id="element-666">
            <div className="w-16 h-16 mx-auto bg-teal-400/20 rounded-2xl flex items-center justify-center mb-6 border border-teal-400/40" data-id="element-667">
              <Workflow className="w-8 h-8 text-teal-400" data-id="element-668" />
            </div>
            <h3 className="text-3xl md:text-4xl font-serif text-white mb-4" data-id="element-669">
              {t('Campaign Service Management', 'Campaign Service Management')}
            </h3>
            <p className="text-xl text-white/80 max-w-2xl mx-auto leading-relaxed" data-id="element-670">
              {t('Managed pharma campaign execution powered by software economics. We deliver the outcome, not the tool.', 'Administreret eksekvering af pharma-kampagner drevet af softwareøkonomi. Vi leverer resultatet, ikke værktøjet.')}
            </p>
          </div>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        x: -20
      }} animate={{
        opacity: stage >= 1 ? 1 : 0,
        x: stage >= 1 ? 0 : -20
      }} className="bg-navy-900/50 border border-navy-700 rounded-2xl p-8 relative overflow-hidden group" data-id="element-671">
          <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity" data-id="element-672">
            <Building2 className="w-24 h-24 text-white" data-id="element-673" />
          </div>
          <h3 className="text-2xl font-serif text-white/50 mb-4 line-through decoration-rose-500/50" data-id="element-674">
            {t('Not an Agency', 'Ikke et bureau')}
          </h3>
          <p className="text-white/50 leading-relaxed" data-id="element-675">
            {t('Traditional agencies are built on billable hours and bespoke creation. They cannot scale down to support the long tail economically.', 'Traditionelle bureauer er bygget på fakturerbare timer og skræddersyet produktion. De kan ikke skalere ned for at understøtte den lange hale økonomisk.')}
          </p>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        x: 20
      }} animate={{
        opacity: stage >= 2 ? 1 : 0,
        x: stage >= 2 ? 0 : 20
      }} className="bg-navy-900/50 border border-navy-700 rounded-2xl p-8 relative overflow-hidden group" data-id="element-676">
          <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity" data-id="element-677">
            <Laptop className="w-24 h-24 text-white" data-id="element-678" />
          </div>
          <h3 className="text-2xl font-serif text-white/50 mb-4 line-through decoration-rose-500/50" data-id="element-679">
            {t('Not a SaaS Tool', 'Ikke et SaaS-værktøj')}
          </h3>
          <p className="text-white/50 leading-relaxed" data-id="element-680">
            {t('Software tools require internal teams to learn, operate, and manage them. Pharma teams lack the capacity to run more tools.', 'Softwareværktøjer kræver, at interne teams lærer, betjener og administrerer dem. Pharma-teams mangler kapaciteten til at køre flere værktøjer.')}
          </p>
        </motion.div>

        <motion.p initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: stage >= 4 ? 1 : 0,
        y: stage >= 4 ? 0 : 10
      }} transition={{
        duration: 0.6
      }} className="text-xl md:text-2xl text-white/70 leading-relaxed max-w-2xl" data-id="element-681">
          Managed pharma campaign execution <br className="hidden md:block" data-id="element-682" />
          powered by{' '}
          <span className="text-teal-400 font-medium" data-id="element-683">software economics.</span>
        </motion.p>

        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: stage >= 5 ? 1 : 0
      }} transition={{
        duration: 0.5
      }} className="mt-12 flex justify-center" data-id="element-684">
          <Button onClick={onContinue} className="px-8 py-3.5" data-id="element-685">
            {t('AI Opportunity', 'AI-muligheden')}
          </Button>
        </motion.div>
      </div>
    </div>;
}